import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { studentAPI } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'sonner';
import {
  User,
  GraduationCap,
  Globe,
  Target,
  Upload,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Info,
  File,
  ExternalLink,
  RefreshCw,
  X,
  AlertCircle,
} from 'lucide-react';

// ─── Constants ──────────────────────────────────────────────────────────────

const STEPS = [
  { key: 'personal', label: 'Personal', icon: User },
  { key: 'academic', label: 'Academic', icon: GraduationCap },
  { key: 'preferences', label: 'Preferences', icon: Globe },
  { key: 'career', label: 'Career', icon: Target },
  { key: 'documents', label: 'Documents', icon: Upload },
  { key: 'complete', label: 'Complete', icon: CheckCircle },
];

const COUNTRY_OPTIONS = [
  'USA',
  'UK',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Netherlands',
  'Sweden',
  'Ireland',
  'Switzerland',
  'New Zealand',
  'Singapore',
];

const NATIONALITY_OPTIONS = [
  'Bangladesh',
  'India',
  'Pakistan',
  'Nepal',
  'Sri Lanka',
  'Myanmar',
  'Nigeria',
  'Ghana',
  'Kenya',
  'Ethiopia',
  'Philippines',
  'Vietnam',
  'Indonesia',
  'Malaysia',
  'Thailand',
  'China',
  'South Korea',
  'Japan',
  'USA',
  'UK',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Other',
];

const BUDGET_OPTIONS = [
  { value: 'under-10000', label: 'Under $10,000' },
  { value: '10000-20000', label: '$10,000 - $20,000' },
  { value: '20000-30000', label: '$20,000 - $30,000' },
  { value: '30000-50000', label: '$30,000 - $50,000' },
  { value: '50000-75000', label: '$50,000 - $75,000' },
  { value: '75000-100000', label: '$75,000 - $100,000' },
  { value: 'over-100000', label: 'Over $100,000' },
];

const INDUSTRY_OPTIONS = [
  { value: 'tech', label: 'Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'other', label: 'Other' },
];

const WORK_LOCATION_OPTIONS = [
  { value: 'study-country', label: 'Stay in Country of Study' },
  { value: 'home-country', label: 'Return to Home Country' },
  { value: 'global', label: 'Open to Global Opportunities' },
  { value: 'other', label: 'Other' },
];

const DOCUMENT_TYPES = [
  {
    type: 'transcripts',
    label: 'Academic Transcripts',
    description: 'Your official academic transcripts',
  },
  {
    type: 'testScores',
    label: 'Test Scores',
    description: 'IELTS, TOEFL, GRE, or other standardized test scores',
  },
  {
    type: 'sop',
    label: 'Statement of Purpose',
    description: 'Your personal statement or SOP',
  },
  {
    type: 'recommendation',
    label: 'Recommendation Letters',
    description: 'Letters of recommendation from professors or employers',
  },
  {
    type: 'resume',
    label: 'Resume/CV',
    description: 'Your current resume or curriculum vitae',
  },
  {
    type: 'passport',
    label: 'Passport Copy',
    description: 'A clear copy of your passport',
  },
];

const ACCENT = '#6366F1';
const ACCENT_SUCCESS = '#22C55E';

// Per-step color themes
const STEP_THEMES = [
  {
    accent: '#6366F1',
    glow: 'rgba(99,102,241,0.28)',
    bg: 'rgba(99,102,241,0.07)',
    border: 'rgba(99,102,241,0.18)',
  },
  {
    accent: '#8B5CF6',
    glow: 'rgba(139,92,246,0.28)',
    bg: 'rgba(139,92,246,0.07)',
    border: 'rgba(139,92,246,0.18)',
  },
  {
    accent: '#3B82F6',
    glow: 'rgba(59,130,246,0.28)',
    bg: 'rgba(59,130,246,0.07)',
    border: 'rgba(59,130,246,0.18)',
  },
  {
    accent: '#0EA5E9',
    glow: 'rgba(14,165,233,0.28)',
    bg: 'rgba(14,165,233,0.07)',
    border: 'rgba(14,165,233,0.18)',
  },
  {
    accent: '#F59E0B',
    glow: 'rgba(245,158,11,0.28)',
    bg: 'rgba(245,158,11,0.07)',
    border: 'rgba(245,158,11,0.18)',
  },
  {
    accent: '#22C55E',
    glow: 'rgba(34,197,94,0.28)',
    bg: 'rgba(34,197,94,0.07)',
    border: 'rgba(34,197,94,0.18)',
  },
];

// ─── Stepper ────────────────────────────────────────────────────────────────

function FormStepper({ currentStep, completedSteps }) {
  return (
    <div className="flex items-center justify-between w-full px-1 sm:px-4">
      {STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isCompleted = completedSteps.includes(idx);
        const isActive = currentStep === idx;
        const lineFilled = idx <= currentStep;

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div
                className="flex-1 h-[2px] mx-1 sm:mx-2 rounded-full overflow-hidden"
                style={{ background: 'rgba(99,102,241,0.12)' }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: lineFilled ? '100%' : '0%',
                    background: `linear-gradient(90deg, ${STEP_THEMES[currentStep].accent}99, ${STEP_THEMES[currentStep].accent})`,
                  }}
                />
              </div>
            )}
            <div className="flex flex-col items-center gap-1.5 shrink-0">
              <div
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-400"
                style={
                  isActive
                    ? {
                        background: `linear-gradient(135deg, ${STEP_THEMES[currentStep].accent} 0%, ${STEP_THEMES[currentStep].accent}cc 100%)`,
                        color: 'white',
                        boxShadow: `0 4px 16px ${STEP_THEMES[currentStep].glow}`,
                      }
                    : isCompleted
                      ? {
                          background: `${STEP_THEMES[currentStep].accent}14`,
                          color: STEP_THEMES[currentStep].accent,
                          boxShadow: 'none',
                        }
                      : {
                          background: 'rgba(99,102,241,0.07)',
                          color: '#9CA3AF',
                        }
                }
              >
                {isCompleted ? (
                  <CheckCircle className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                ) : (
                  <Icon className="w-4.5 h-4.5 sm:w-5 sm:h-5" />
                )}
              </div>
              <span
                className="text-[10px] sm:text-xs font-semibold transition-colors duration-300"
                style={{
                  color: isActive
                    ? STEP_THEMES[currentStep].accent
                    : isCompleted
                      ? STEP_THEMES[currentStep].accent
                      : '#9CA3AF',
                }}
              >
                {step.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Document Upload Item ───────────────────────────────────────────────────

function DocUploadItem({
  docType,
  label,
  description,
  currentFile,
  onUpload,
  disabled,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const hasFile = !!currentFile;

  const handleFileChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      await onUpload(file, docType);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div
      className="rounded-2xl p-4 transition-all duration-300"
      style={
        hasFile
          ? {
              background:
                'linear-gradient(135deg, rgba(34,197,94,0.07) 0%, rgba(16,185,129,0.04) 100%)',
              border: '1.5px solid rgba(34,197,94,0.22)',
              boxShadow:
                '0 2px 14px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.65)',
            }
          : {
              background:
                'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.42) 100%)',
              border: '1.5px dashed rgba(99,102,241,0.2)',
              boxShadow:
                '0 2px 14px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
              backdropFilter: 'blur(8px)',
            }
      }
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
            {label}
            {hasFile && (
              <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded-full border border-emerald-200/60">
                Already uploaded
              </span>
            )}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>

      {hasFile ? (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => window.open(currentFile, '_blank')}
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
          >
            <ExternalLink className="w-3 h-3" /> View
          </button>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || uploading}
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 font-medium disabled:opacity-50"
          >
            <RefreshCw className="w-3 h-3" /> Replace existing document
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className="mt-3 w-full flex flex-col items-center justify-center py-4 rounded-xl cursor-pointer transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: 'rgba(99,102,241,0.04)',
            border: '1px dashed rgba(99,102,241,0.18)',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(99,102,241,0.08)';
            e.currentTarget.style.borderColor = 'rgba(99,102,241,0.28)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'rgba(99,102,241,0.04)';
            e.currentTarget.style.borderColor = 'rgba(99,102,241,0.18)';
          }}
        >
          {uploading ? (
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          ) : (
            <>
              <File className="w-6 h-6 text-indigo-300 mb-1" />
              <span className="text-xs text-gray-500 font-medium">
                Drag & drop or click to upload
              </span>
              <span className="text-[10px] text-gray-400 mt-0.5">
                PDF, DOC, DOCX, JPG, PNG (max 10MB)
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || uploading}
      />
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ProfileAssessmentModal({
  open,
  onOpenChange,
  onApplySuccess,
  // Optional overrides for reuse outside student flow (e.g. partner-initiated)
  profileSource,      // async () => ({ student }) — replaces studentAPI.getMyProfile
  onCustomSubmit,     // async ({ profilePayload, documents, formData }) => void — replaces default updateMyProfile + onApplySuccess
  submitLabel,        // optional button label override for the Complete step
  showRepresentativeNameField = true,
}) {
  const { user } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [errors, setErrors] = useState({});
  const [representativeName, setRepresentativeName] = useState('');

  // Form data
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    age: '',
    nationality: '',
    currentEducationLevel: '',
    fieldOfStudy: '',
    gpa: '',
    graduationYear: '',
    institution: '',
    ielts: '',
    toefl: '',
    gre: '',
    preferredCountries: [],
    preferredDegreeLevel: '',
    budget: '',
    careerGoals: '',
    industry: '',
    workLocation: '',
  });

  // Documents state (URLs from profile)
  const [documents, setDocuments] = useState({});

  // Fetch profile when modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setErrors({});
      setRepresentativeName('');
      fetchProfile();
    }
  }, [open]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = profileSource
        ? await profileSource()
        : await studentAPI.getMyProfile();
      const p = response.student || {};
      setProfileData(p);

      const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ');
      setFormData({
        fullName: fullName || '',
        email: p.email || user?.email || '',
        phone: p.phone || '',
        age: p.age ? String(p.age) : '',
        nationality: p.country || '',
        currentEducationLevel: p.currentEducationLevel || '',
        fieldOfStudy: p.fieldOfStudy || '',
        gpa: p.gpa || '',
        graduationYear: p.graduationYear ? String(p.graduationYear) : '',
        institution: p.institution || '',
        ielts: p.ielts || '',
        toefl: p.toefl || '',
        gre: p.gre || '',
        preferredCountries: p.preferredCountries || [],
        preferredDegreeLevel: p.preferredDegreeLevel || '',
        budget: p.budget || '',
        careerGoals: p.careerGoals || '',
        industry: p.industry || '',
        workLocation: p.workLocation || '',
      });

      setDocuments(p.documents || {});
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  // ─── Field handlers ─────────────────────────────────────────────────────

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const toggleCountry = country => {
    setFormData(prev => {
      const list = prev.preferredCountries.includes(country)
        ? prev.preferredCountries.filter(c => c !== country)
        : [...prev.preferredCountries, country];
      return { ...prev, preferredCountries: list };
    });
    if (errors.preferredCountries)
      setErrors(prev => ({ ...prev, preferredCountries: null }));
  };

  const handleDocUpload = async (file, documentType) => {
    const response = await studentAPI.uploadDocument(file, documentType);
    setDocuments(prev => ({
      ...prev,
      [documentType]: response.file?.url || response.url,
    }));
    toast.success(`${documentType} uploaded successfully`);
  };

  // ─── Validation ─────────────────────────────────────────────────────────

  const validateStep = step => {
    const errs = {};

    if (step === 0) {
      if (!formData.fullName.trim()) errs.fullName = 'Full name is required';
      if (!formData.phone.trim()) errs.phone = 'Phone number is required';
      if (
        !formData.age ||
        isNaN(formData.age) ||
        Number(formData.age) < 1 ||
        Number(formData.age) > 120
      )
        errs.age = 'Valid age is required';
      if (!formData.nationality) errs.nationality = 'Nationality is required';
    }

    if (step === 1) {
      if (!formData.currentEducationLevel)
        errs.currentEducationLevel = 'Education level is required';
      if (!formData.fieldOfStudy.trim())
        errs.fieldOfStudy = 'Field of study is required';
      if (!formData.gpa.trim()) errs.gpa = 'GPA is required';
      if (!formData.graduationYear || isNaN(formData.graduationYear))
        errs.graduationYear = 'Graduation year is required';
      if (!formData.institution.trim())
        errs.institution = 'Institution is required';
    }

    if (step === 2) {
      if (!formData.preferredCountries.length)
        errs.preferredCountries = 'Select at least one country';
      if (!formData.preferredDegreeLevel)
        errs.preferredDegreeLevel = 'Degree level is required';
      if (!formData.budget) errs.budget = 'Budget range is required';
    }

    if (step === 3) {
      if (!formData.careerGoals.trim())
        errs.careerGoals = 'Career objectives are required';
      if (!formData.industry) errs.industry = 'Target industry is required';
      if (!formData.workLocation)
        errs.workLocation = 'Work location preference is required';
    }

    // Step 4 (documents) — no required fields
    // Step 5 (complete) — no validation

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── Navigation ─────────────────────────────────────────────────────────

  const handleNext = () => {
    if (currentStep < 4 && !validateStep(currentStep)) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(s => s + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) setCurrentStep(s => s - 1);
  };

  const handleSaveAndExit = () => {
    onOpenChange(false);
  };

  // ─── Final Submit ───────────────────────────────────────────────────────

  const handleFinish = async () => {
    setSubmitting(true);
    try {
      // Split fullName into firstName and lastName
      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Build profile update payload
      const profilePayload = {
        firstName,
        lastName,
        phone: formData.phone,
        country: formData.nationality,
        age: formData.age ? Number(formData.age) : undefined,
        currentEducationLevel: formData.currentEducationLevel,
        fieldOfStudy: formData.fieldOfStudy,
        gpa: formData.gpa,
        graduationYear: formData.graduationYear
          ? Number(formData.graduationYear)
          : undefined,
        institution: formData.institution,
        ielts: formData.ielts || undefined,
        toefl: formData.toefl || undefined,
        gre: formData.gre || undefined,
        preferredCountries: formData.preferredCountries,
        preferredDegreeLevel: formData.preferredDegreeLevel,
        budget: formData.budget,
        careerGoals: formData.careerGoals,
        industry: formData.industry,
        workLocation: formData.workLocation,
      };

      if (onCustomSubmit) {
        // Partner / staff-initiated flow — delegate fully to caller
        await onCustomSubmit({ profilePayload, documents, formData });
      } else {
        // Default student flow: save own profile, then trigger application
        await studentAPI.updateMyProfile(profilePayload);
        const repName =
          typeof representativeName === 'string' && representativeName.trim()
            ? representativeName.trim()
            : undefined;
        await onApplySuccess(null, repName);
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to submit application',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Computed values for Complete step ──────────────────────────────────

  const computeStats = useCallback(() => {
    const fields = [
      formData.fullName,
      formData.phone,
      formData.age,
      formData.nationality,
      formData.currentEducationLevel,
      formData.fieldOfStudy,
      formData.gpa,
      formData.graduationYear,
      formData.institution,
      formData.preferredDegreeLevel,
      formData.budget,
      formData.careerGoals,
      formData.industry,
      formData.workLocation,
    ];
    const filledFields = fields.filter(v => v && String(v).trim()).length;
    const totalFields = fields.length;
    const optionalFields = [
      formData.ielts,
      formData.toefl,
      formData.gre,
    ].filter(v => v && String(v).trim()).length;
    const docsCount = Object.values(documents).filter(Boolean).length;
    const profilePercent = Math.round(
      ((filledFields + optionalFields) / (totalFields + 3)) * 100,
    );

    return {
      filledFields: filledFields + optionalFields,
      profilePercent,
      docsCount,
    };
  }, [formData, documents]);

  // ─── Shared input class helper ──────────────────────────────────────────

  const inputCls = hasError =>
    `bg-white/70 border transition-all duration-200 ${
      hasError
        ? 'border-red-400'
        : 'border-white/60 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100'
    }`;

  // ─── Step Content Renderers ─────────────────────────────────────────────

  const renderPersonalStep = () => {
    const theme = STEP_THEMES[0];
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}bb 100%)`,
              boxShadow: `0 6px 22px ${theme.glow}`,
            }}
          >
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Personal Information
            </h3>
            <p className="text-sm text-gray-500">
              Tell us about yourself to get started
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-red-500 after:content-['*']">Full Name </Label>
          <Input
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter your full name"
            className={inputCls(errors.fullName)}
          />
          {errors.fullName && (
            <p className="text-xs text-red-500">{errors.fullName}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Email Address{' '}
            </Label>
            <Input
              value={formData.email}
              disabled
              className="bg-gray-50/60 border-white/50 text-gray-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Phone Number{' '}
            </Label>
            <Input
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="e.g. 01XXXXXXXXX"
              className={inputCls(errors.phone)}
            />
            {errors.phone && (
              <p className="text-xs text-red-500">{errors.phone}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">Age </Label>
            <Input
              name="age"
              type="number"
              value={formData.age}
              onChange={handleChange}
              placeholder="Your age"
              min="1"
              max="120"
              className={inputCls(errors.age)}
            />
            {errors.age && <p className="text-xs text-red-500">{errors.age}</p>}
          </div>
          {showRepresentativeNameField && (
            <div className="space-y-2">
              <Label className="text-gray-700 font-semibold">
                Representative Name (Optional)
              </Label>
              <Input
                value={representativeName}
                onChange={(e) => setRepresentativeName(e.target.value)}
                placeholder="Enter representative name (if any)"
                className={inputCls(false)}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Nationality{' '}
            </Label>
            <Select
              value={formData.nationality}
              onValueChange={v => handleSelectChange('nationality', v)}
            >
              <SelectTrigger className={inputCls(errors.nationality)}>
                <SelectValue placeholder="Select nationality" />
              </SelectTrigger>
              <SelectContent>
                {NATIONALITY_OPTIONS.map(n => (
                  <SelectItem key={n} value={n}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.nationality && (
              <p className="text-xs text-red-500">{errors.nationality}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderAcademicStep = () => {
    const theme = STEP_THEMES[1];
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}bb 100%)`,
              boxShadow: `0 6px 22px ${theme.glow}`,
            }}
          >
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Academic Background
            </h3>
            <p className="text-sm text-gray-500">
              Your educational history and achievements
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Current Education Level{' '}
            </Label>
            <Select
              value={formData.currentEducationLevel}
              onValueChange={v =>
                handleSelectChange('currentEducationLevel', v)
              }
            >
              <SelectTrigger className={inputCls(errors.currentEducationLevel)}>
                <SelectValue placeholder="Select level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="diploma">Diploma</SelectItem>
                <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                <SelectItem value="master">Master's Degree</SelectItem>
                <SelectItem value="phd">PhD</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.currentEducationLevel && (
              <p className="text-xs text-red-500">
                {errors.currentEducationLevel}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Field of Study{' '}
            </Label>
            <Input
              name="fieldOfStudy"
              value={formData.fieldOfStudy}
              onChange={handleChange}
              placeholder="e.g. Computer Science"
              className={inputCls(errors.fieldOfStudy)}
            />
            {errors.fieldOfStudy && (
              <p className="text-xs text-red-500">{errors.fieldOfStudy}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              GPA/Percentage{' '}
            </Label>
            <Input
              name="gpa"
              value={formData.gpa}
              onChange={handleChange}
              placeholder="e.g. 3.8"
              className={inputCls(errors.gpa)}
            />
            {errors.gpa && <p className="text-xs text-red-500">{errors.gpa}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Graduation Year{' '}
            </Label>
            <Input
              name="graduationYear"
              type="number"
              value={formData.graduationYear}
              onChange={handleChange}
              placeholder="e.g. 2025"
              min="1900"
              max={new Date().getFullYear() + 10}
              className={inputCls(errors.graduationYear)}
            />
            {errors.graduationYear && (
              <p className="text-xs text-red-500">{errors.graduationYear}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Institution{' '}
            </Label>
            <Input
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              placeholder="Your university"
              className={inputCls(errors.institution)}
            />
            {errors.institution && (
              <p className="text-xs text-red-500">{errors.institution}</p>
            )}
          </div>
        </div>

        <div
          className="pt-3 pb-1 px-4 rounded-2xl"
          style={{
            background: `linear-gradient(135deg, ${theme.bg} 0%, rgba(255,255,255,0.4) 100%)`,
            border: `1px solid ${theme.border}`,
          }}
        >
          <h4
            className="text-sm font-semibold mb-3"
            style={{ color: theme.accent }}
          >
            Standardized Test Scores{' '}
            <span className="font-normal text-gray-400">(Optional)</span>
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-3">
            <div className="space-y-2">
              <Label>IELTS Score</Label>
              <Input
                name="ielts"
                value={formData.ielts}
                onChange={handleChange}
                placeholder="e.g. 7.5"
                className={inputCls(false)}
              />
            </div>
            <div className="space-y-2">
              <Label>TOEFL Score</Label>
              <Input
                name="toefl"
                value={formData.toefl}
                onChange={handleChange}
                placeholder="e.g. 100"
                className={inputCls(false)}
              />
            </div>
            <div className="space-y-2">
              <Label>GRE Score</Label>
              <Input
                name="gre"
                value={formData.gre}
                onChange={handleChange}
                placeholder="e.g. 320"
                className={inputCls(false)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPreferencesStep = () => {
    const theme = STEP_THEMES[2];
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}bb 100%)`,
              boxShadow: `0 6px 22px ${theme.glow}`,
            }}
          >
            <Globe className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Study Preferences
            </h3>
            <p className="text-sm text-gray-500">
              Where and what would you like to study?
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-red-500 after:content-['*']">
            Preferred Countries{' '}
          </Label>
          <p className="text-xs text-gray-500 -mt-1">
            Select at least one country you'd like to study in
          </p>
          {errors.preferredCountries && (
            <p className="text-xs text-red-500">{errors.preferredCountries}</p>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {COUNTRY_OPTIONS.map(country => {
              const selected = formData.preferredCountries.includes(country);
              return (
                <button
                  key={country}
                  type="button"
                  onClick={() => toggleCountry(country)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                  style={
                    selected
                      ? {
                          background: `linear-gradient(135deg, ${theme.bg}, rgba(255,255,255,0.6))`,
                          border: `1.5px solid ${theme.border}`,
                          color: theme.accent,
                          boxShadow: `0 2px 10px ${theme.glow}`,
                        }
                      : {
                          background: 'rgba(255,255,255,0.65)',
                          border: '1.5px solid rgba(255,255,255,0.6)',
                          color: '#6B7280',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                        }
                  }
                >
                  <span
                    className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200"
                    style={
                      selected
                        ? {
                            borderColor: theme.accent,
                            background: theme.accent,
                          }
                        : { borderColor: '#D1D5DB' }
                    }
                  >
                    {selected && (
                      <span className="w-1.5 h-1.5 rounded-full bg-white" />
                    )}
                  </span>
                  {country}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-red-500 after:content-['*']">
              Preferred Degree Level{' '}
            </Label>
            {errors.preferredDegreeLevel && (
              <p className="text-xs text-red-500">
                {errors.preferredDegreeLevel}
              </p>
            )}
            <div className="space-y-2">
              {[
                { value: 'bachelor', label: "Bachelor's Degree" },
                { value: 'master', label: "Master's Degree" },
                { value: 'phd', label: 'PhD' },
                { value: 'other', label: 'Other' },
              ].map(opt => {
                const sel = formData.preferredDegreeLevel === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() =>
                      handleSelectChange('preferredDegreeLevel', opt.value)
                    }
                    className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200"
                    style={
                      sel
                        ? {
                            background: `linear-gradient(135deg, ${theme.bg}, rgba(255,255,255,0.5))`,
                            border: `1.5px solid ${theme.border}`,
                            color: theme.accent,
                            boxShadow: `0 2px 10px ${theme.glow}`,
                          }
                        : {
                            background: 'rgba(255,255,255,0.55)',
                            border: '1.5px solid rgba(255,255,255,0.5)',
                            color: '#6B7280',
                          }
                    }
                  >
                    <span
                      className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all duration-200"
                      style={{ borderColor: sel ? theme.accent : '#D1D5DB' }}
                    >
                      {sel && (
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: theme.accent }}
                        />
                      )}
                    </span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Budget Range (USD/Year){' '}
            </Label>
            <Select
              value={formData.budget}
              onValueChange={v => handleSelectChange('budget', v)}
            >
              <SelectTrigger className={inputCls(errors.budget)}>
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                {BUDGET_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.budget && (
              <p className="text-xs text-red-500">{errors.budget}</p>
            )}
            <div
              className="mt-2 px-3 py-2.5 rounded-xl flex items-start gap-2"
              style={{
                background: theme.bg,
                border: `1px solid ${theme.border}`,
              }}
            >
              <Info
                className="w-3.5 h-3.5 mt-0.5 shrink-0"
                style={{ color: theme.accent }}
              />
              <p
                className="text-xs leading-relaxed"
                style={{ color: theme.accent }}
              >
                This helps us recommend suitable programs and scholarship
                opportunities
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCareerStep = () => {
    const theme = STEP_THEMES[3];
    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}bb 100%)`,
              boxShadow: `0 6px 22px ${theme.glow}`,
            }}
          >
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">Career Goals</h3>
            <p className="text-sm text-gray-500">
              What are your professional aspirations?
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-red-500 after:content-['*']">
            Career Objectives{' '}
          </Label>
          <Textarea
            name="careerGoals"
            value={formData.careerGoals}
            onChange={handleChange}
            placeholder="Be specific about your goals to help us recommend the best programs"
            rows={4}
            className={inputCls(errors.careerGoals)}
          />
          <p className="text-xs text-gray-400 mt-1">
            Be specific about your goals to help us recommend the best programs
          </p>
          {errors.careerGoals && (
            <p className="text-xs text-red-500">{errors.careerGoals}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Target Industry{' '}
            </Label>
            <Select
              value={formData.industry}
              onValueChange={v => handleSelectChange('industry', v)}
            >
              <SelectTrigger className={inputCls(errors.industry)}>
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.industry && (
              <p className="text-xs text-red-500">{errors.industry}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-red-500 after:content-['*']">
              Preferred Work Location{' '}
            </Label>
            <Select
              value={formData.workLocation}
              onValueChange={v => handleSelectChange('workLocation', v)}
            >
              <SelectTrigger className={inputCls(errors.workLocation)}>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {WORK_LOCATION_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.workLocation && (
              <p className="text-xs text-red-500">{errors.workLocation}</p>
            )}
          </div>
        </div>

        <div
          className="rounded-2xl p-3.5 flex items-start gap-2.5"
          style={{
            background: `linear-gradient(135deg, ${theme.bg}, rgba(255,255,255,0.5))`,
            border: `1px solid ${theme.border}`,
            boxShadow: `0 2px 12px ${theme.glow}`,
          }}
        >
          <Info
            className="w-4 h-4 mt-0.5 shrink-0"
            style={{ color: theme.accent }}
          />
          <p
            className="text-xs leading-relaxed"
            style={{ color: theme.accent }}
          >
            <span className="font-semibold">Tip:</span> Having clear career
            goals helps our advisors recommend programs with strong industry
            connections, internship opportunities, and career support services
            aligned with your aspirations.
          </p>
        </div>
      </div>
    );
  };

  const renderDocumentsStep = () => {
    const theme = STEP_THEMES[4];
    // Split into uploaded and not-uploaded
    const uploaded = DOCUMENT_TYPES.filter(d => documents[d.type]);
    const notUploaded = DOCUMENT_TYPES.filter(d => !documents[d.type]);

    return (
      <div className="space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}bb 100%)`,
              boxShadow: `0 6px 22px ${theme.glow}`,
            }}
          >
            <Upload className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-800">
              Upload Documents
            </h3>
            <p className="text-sm text-gray-500">
              Add your documents — you can skip this step
            </p>
          </div>
        </div>

        <div
          className="rounded-2xl p-3.5 flex items-start gap-2.5"
          style={{
            background: `linear-gradient(135deg, ${theme.bg}, rgba(255,255,255,0.5))`,
            border: `1px solid ${theme.border}`,
            boxShadow: `0 2px 12px ${theme.glow}`,
          }}
        >
          <Info
            className="w-4 h-4 mt-0.5 shrink-0"
            style={{ color: theme.accent }}
          />
          <p
            className="text-xs leading-relaxed"
            style={{ color: theme.accent }}
          >
            <span className="font-semibold">Note:</span> Document upload is
            optional. You can skip this step and upload documents later from
            profile settings.
          </p>
        </div>

        {uploaded.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700">
              Previously Uploaded Documents
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {uploaded.map(doc => (
                <DocUploadItem
                  key={doc.type}
                  docType={doc.type}
                  label={doc.label}
                  description={doc.description}
                  currentFile={documents[doc.type]}
                  onUpload={handleDocUpload}
                />
              ))}
            </div>
          </div>
        )}

        {notUploaded.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-gray-700">
              Upload Additional Documents
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {notUploaded.map(doc => (
                <DocUploadItem
                  key={doc.type}
                  docType={doc.type}
                  label={doc.label}
                  description={doc.description}
                  currentFile={null}
                  onUpload={handleDocUpload}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCompleteStep = () => {
    const theme = STEP_THEMES[5];
    const stats = computeStats();

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-bold text-gray-800">
            Profile Assessment
          </h3>
          <p className="text-sm text-gray-500">
            Complete your profile to get personalized recommendations
          </p>
        </div>

        <div className="text-center py-5">
          <div
            className="w-18 h-18 rounded-3xl flex items-center justify-center mx-auto mb-4"
            style={{
              width: 72,
              height: 72,
              background: `linear-gradient(135deg, ${ACCENT_SUCCESS}22 0%, ${ACCENT_SUCCESS}10 100%)`,
              border: `2px solid ${ACCENT_SUCCESS}30`,
              boxShadow: `0 8px 28px ${ACCENT_SUCCESS}20`,
            }}
          >
            <CheckCircle
              className="w-9 h-9"
              style={{ color: ACCENT_SUCCESS }}
            />
          </div>
          <h2 className="text-2xl font-bold text-gray-800">All Set!</h2>
          <p className="text-sm text-gray-500 mt-1.5 max-w-md mx-auto leading-relaxed">
            Your profile assessment has been completed successfully. Our team
            will review your information and provide personalized
            recommendations soon.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            {
              icon: Target,
              value: `${stats.profilePercent}%`,
              label: 'Profile Complete',
              theme: STEP_THEMES[0],
            },
            {
              icon: CheckCircle,
              value: stats.filledFields,
              label: 'Fields Completed',
              theme: STEP_THEMES[2],
            },
            {
              icon: File,
              value: stats.docsCount,
              label: 'Documents Uploaded',
              theme: STEP_THEMES[5],
            },
          ].map(({ icon: Icon, value, label, theme: t }, i) => (
            <div
              key={i}
              className="text-center p-4 rounded-2xl transition-all duration-200"
              style={{
                background: `linear-gradient(135deg, ${t.bg} 0%, rgba(255,255,255,0.6) 100%)`,
                border: `1px solid ${t.border}`,
                boxShadow: `0 2px 12px ${t.glow}`,
              }}
            >
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center mx-auto mb-2"
                style={{ background: `${t.accent}16` }}
              >
                <Icon className="w-4 h-4" style={{ color: t.accent }} />
              </div>
              <p className="text-xl font-bold" style={{ color: t.accent }}>
                {value}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">{label}</p>
            </div>
          ))}
        </div>

        {/* Profile Summary */}
        <div
          className="rounded-2xl p-4 space-y-3"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0.75) 0%, rgba(240,240,255,0.55) 100%)',
            border: '1px solid rgba(99,102,241,0.12)',
            boxShadow:
              '0 2px 16px rgba(99,102,241,0.06), inset 0 1px 0 rgba(255,255,255,0.9)',
          }}
        >
          <h4 className="text-sm font-bold text-gray-800">
            Your Profile Summary
          </h4>
          <p className="text-xs text-gray-500">
            Here's what we collected from your assessment
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="font-semibold text-gray-700">
                Study Preferences
              </span>
              <ul className="text-gray-500 text-xs mt-1 space-y-0.5">
                <li>
                  Countries: {formData.preferredCountries.join(', ') || '—'}
                </li>
                <li>Degree Level: {formData.preferredDegreeLevel || '—'}</li>
                <li>
                  Budget:{' '}
                  {BUDGET_OPTIONS.find(b => b.value === formData.budget)
                    ?.label || '—'}
                </li>
              </ul>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                Academic Background
              </span>
              <ul className="text-gray-500 text-xs mt-1 space-y-0.5">
                <li>Education: {formData.currentEducationLevel || '—'}</li>
                <li>Field: {formData.fieldOfStudy || '—'}</li>
                <li>GPA: {formData.gpa || '—'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Step content map ───────────────────────────────────────────────────

  const stepContent = [
    renderPersonalStep,
    renderAcademicStep,
    renderPreferencesStep,
    renderCareerStep,
    renderDocumentsStep,
    renderCompleteStep,
  ];

  const completedSteps = [];
  for (let i = 0; i < currentStep; i++) completedSteps.push(i);

  const isLastStep = currentStep === STEPS.length - 1;
  const currentTheme = STEP_THEMES[currentStep];

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[92vh] p-0 rounded-[28px] overflow-hidden gap-0 border-0"
        style={{
          background:
            'linear-gradient(145deg, #f0efff 0%, #f5f3ff 40%, #eef2ff 80%, #f0f4ff 100%)',
          boxShadow:
            '0 32px 80px rgba(99,102,241,0.18), 0 8px 40px rgba(0,0,0,0.14)',
        }}
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogTitle className="sr-only">
          Profile Assessment Application
        </DialogTitle>

        {/* Stepper Header */}
        <div
          className="px-4 sm:px-6 py-5 border-b relative overflow-hidden"
          style={{
            background:
              'linear-gradient(135deg, #d6d3f5 0%, #ddd9f9 45%, #d8d4f6 100%)',
            borderColor: 'rgba(99,102,241,0.2)',
            boxShadow: '0 4px 18px rgba(99,102,241,0.1)',
          }}
        >
          <FormStepper
            currentStep={currentStep}
            completedSteps={completedSteps}
          />
        </div>

        {/* Step Content */}
        <div
          className="overflow-y-auto px-6 sm:px-10 py-6"
          style={{ maxHeight: 'calc(92vh - 180px)', background: 'transparent' }}
        >
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2
                className="w-8 h-8 animate-spin"
                style={{ color: ACCENT }}
              />
            </div>
          ) : (
            stepContent[currentStep]()
          )}
        </div>

        {/* Footer Navigation */}
        <div
          className="border-t px-6 sm:px-10 py-4 flex items-center justify-between relative"
          style={{
            background: 'linear-gradient(135deg, #d6d3f5 0%, #ddd9f9 100%)',
            borderColor: 'rgba(99,102,241,0.2)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 -4px 24px rgba(99,102,241,0.1)',
          }}
        >
          <div>
            {currentStep > 0 && (
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrevious}
                disabled={submitting}
                className="rounded-xl text-gray-600 hover:text-gray-800 hover:bg-white/70 transition-all duration-200"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isLastStep && (
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveAndExit}
                className="rounded-xl border-white/70 bg-white/60 hover:bg-white/80 text-gray-600 transition-all duration-200"
                style={{ backdropFilter: 'blur(8px)' }}
              >
                Save & Exit
              </Button>
            )}

            {isLastStep ? (
              <Button
                type="button"
                onClick={handleFinish}
                disabled={submitting}
                className="rounded-xl px-6 text-white border-0 hover:opacity-90 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT_SUCCESS} 0%, #16a34a 100%)`,
                  boxShadow: `0 6px 22px rgba(34,197,94,0.32)`,
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Application{' '}
                    <CheckCircle className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                className="rounded-xl px-6 text-white border-0 hover:opacity-90 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer"
                style={{
                  background: `linear-gradient(135deg, ${currentTheme.accent} 0%, ${currentTheme.accent}cc 100%)`,
                  boxShadow: `0 6px 22px ${currentTheme.glow}`,
                }}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
